import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { AccountRegistrationService } from '../../services/account-registration.service';
import { Subscription } from 'rxjs';
import { ErrorReportingService, NewAccountEntityType, NewAccountType, WizardButtonActions, WizardButtonOptions } from 'common';
import { ILicenceTypeData } from '../../interfaces/account-registration/licence-type-data.interface';
import { IGeneralDetailsData } from '../../interfaces/account-registration/general-details-data.interface';
import { IFinancialData } from '../../interfaces/account-registration/financial-data.interface';
import { IContactDetailsData } from '../../interfaces/account-registration/contact-details-data.interface';
import { IPrsData } from '../../interfaces/account-registration/prs-data.interface';

@Component({
  selector: 'acc-registration',
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss',
})
export class RegistrationComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper: MatStepper;
  private subscription = new Subscription();

  submitted: boolean = false;

  isEditable = true;

  showRadioTabs: boolean;
  entityType: NewAccountEntityType;
  showPrsTabs: boolean;
  numberOfPrsContacts: number = 2;
  isCompany: boolean = true;

  radioContactQualifierHtml: string;
  prsContact1QualifierHtml: string;
  prsContact2QualifierHtml: string;
  prsContact3QualifierHtml: string;

  wizardButtonOptions: WizardButtonOptions = {
    showSubmitButtonOnLastStep: true,
    submitButtonText: 'Submit',
  };

  get accountTypeSelected(): boolean {
    return this.showRadioTabs || this.showPrsTabs;
  }

  get formGroupAccountType() {
    return this.accountRegistrationService.formGroupAccountType;
  }

  get formGroupLicenceType(): FormGroup<ILicenceTypeData> {
    return this.accountRegistrationService.formLicenceType;
  }

  get formGeneralDetails(): FormGroup<IGeneralDetailsData> {
    return this.accountRegistrationService.formGeneralDetails;
  }

  get formFinancialDetails(): FormGroup<IFinancialData> {
    return this.accountRegistrationService.formFinancialDetails;
  }

  get formRadioContactDetails(): FormGroup<IContactDetailsData> {
    return this.accountRegistrationService.formRadioContactDetails;
  }

  get formPrsContact1Details(): FormGroup<IContactDetailsData> {
    return this.accountRegistrationService.formPrsContactDetails[0];
  }

  get formPrsContact2Details(): FormGroup<IContactDetailsData> {
    return this.accountRegistrationService.formPrsContactDetails[1];
  }

  get formPrsContact3Details(): FormGroup<IContactDetailsData> {
    return this.accountRegistrationService.formPrsContactDetails[2];
  }

  get formPrsDetails(): FormGroup<IPrsData> {
    return this.accountRegistrationService.formPrsDetails;
  }

  constructor(
    private readonly accountRegistrationService: AccountRegistrationService,
    private readonly errorReporting: ErrorReportingService
  ) {}

  ngOnInit(): void {
    this.registerAccountTypeListener();
    this.registerEntityTypeListener();
    this.registerPrsListeners();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  registerAccountTypeListener(): void {
    this.subscription.add(
      this.accountRegistrationService.accountType$.subscribe((accountType: NewAccountType) => {
        this.showRadioTabs = accountType === NewAccountType.Radio;
        this.showPrsTabs = accountType === NewAccountType.Prs;
      })
    );
  }

  registerPrsListeners(): void {
    this.subscription.add(
      this.accountRegistrationService.numberOfPrsContacts$.subscribe((numberOfPrsContacts) => {
        this.numberOfPrsContacts = numberOfPrsContacts;
      })
    );
  }

  registerEntityTypeListener(): void {
    this.subscription.add(
      this.accountRegistrationService.entityType$.subscribe((entityType) => {
        this.entityType = entityType;
        this.isCompany = entityType === NewAccountEntityType.Company;
      })
    );
  }

  wizardButtonAction(event) {
    switch (event) {
      case WizardButtonActions.Previous:
        this.accountRegistrationService.previousStep(this.stepper);
        break;
      case WizardButtonActions.Next:
        this.markFormGroupAsTouched();
        this.accountRegistrationService.nextStep(this.stepper);
        break;
      case WizardButtonActions.Submit:
        this.accountRegistrationService
          .submit()
          .then(() => {
            this.submitted = true;
          })
          .catch((err) => {
            this.errorReporting.logError(err, 'There was an error submitting your application.', 'NewAccountRequest');
          });
        break;
    }
  }

  private markFormGroupAsTouched(): void {
    if (this.showPrsTabs) {
      switch (this.stepper.selectedIndex) {
        case 1:
          this.formGeneralDetails.markAllAsTouched();
          break;
        case 3:
          this.accountRegistrationService.formPrsContactDetails[0].markAllAsTouched();
          break;
        case 4:
          this.accountRegistrationService.formPrsContactDetails[1].markAllAsTouched();
          break;
        case 5:
          this.accountRegistrationService.formPrsContactDetails[2].markAllAsTouched();
          break;
        case 6:
          if (this.numberOfPrsContacts === 3) this.accountRegistrationService.formPrsContactDetails[3].markAllAsTouched();
          break;
      }
    } else if (this.showRadioTabs) {
      switch (this.stepper.selectedIndex) {
        case 2:
          this.formGeneralDetails.markAllAsTouched();
          break;
        case 3:
          this.accountRegistrationService.formFinancialDetails.markAllAsTouched();
          break;
        case 4:
          this.accountRegistrationService.formRadioContactDetails.markAllAsTouched();
          break;
      }
    }
  }
}
